import * as React from 'react';
import { bareListDecorator } from 'webapp/decorators/list';
import { styled } from '@glitz/react';
import { animate } from '@avensia/excite-ui';
import { createMinLargeViewportDecorator } from 'webapp/viewport';
import { highlightText } from 'webapp/search/highlight-text';
import { grey } from 'webapp/shared/sharedTheme';
import { SUGGESTIONS_ID } from './QuickSearch';

export const SEARCH_HISTORY_COUNT = 3;

export type AllSuggestionsType = NordicNest.Api.SearchSuggestionApiModel & {
  usedHistory: boolean;
};

type PropType = {
  searchText: string;
  visible: boolean;
  applySuggestion: (phrase: string, usedHistory?: boolean) => void;
  highlightedIndex: number;
  allSuggestions: AllSuggestionsType[];
  handleKeyDown: (e: KeyboardEvent) => void;
};

const animationOptions: KeyframeAnimationOptions = {
  fill: 'forwards',
  duration: 200,
};

export const Suggestions = styled(
  ({ searchText, applySuggestion, visible, allSuggestions, highlightedIndex, handleKeyDown }: PropType) => {
    const elementRef = React.useRef<HTMLDivElement>(null);
    const animateRef = React.useRef<HTMLDivElement>(null);

    React.useEffect(() => {
      if (elementRef.current && animateRef.current) {
        const style = {
          height: visible && allSuggestions.length > 0 ? `${elementRef.current.scrollHeight}px` : 0,
          overflow: 'hidden',
          borderWidth: visible && allSuggestions.length ? 'thin' : 0,
        };
        animate(animateRef.current, style, animationOptions);
      }
    }, [visible, allSuggestions.length]);

    React.useEffect(() => {
      const localHandleKeyDown = (e: KeyboardEvent) => {
        handleKeyDown(e);
      };

      window.addEventListener('keydown', localHandleKeyDown);
      return () => {
        window.removeEventListener('keydown', localHandleKeyDown);
      };
    }, [handleKeyDown]);

    return (
      <Base ref={animateRef}>
        <Container ref={elementRef}>
          {allSuggestions.length > 0 && (
            <List role="listbox" id={SUGGESTIONS_ID}>
              {allSuggestions.map((s, index) => {
                return (
                  <Suggestion
                    role="option"
                    id={getSuggestionId(index, s.text)}
                    key={`${s.text}_${index}`}
                    onClick={e => {
                      e.preventDefault();
                      e.stopPropagation();
                      applySuggestion(s.text, s.usedHistory);
                    }}
                    css={highlightedIndex === index && highlightedDecorator}
                    aria-selected={highlightedIndex === index}
                  >
                    {highlightText(s.text, searchTextToWords(searchText))}
                  </Suggestion>
                );
              })}
            </List>
          )}
        </Container>
      </Base>
    );
  },
);

export const getSuggestionId = (index: number, text: string) => `${index}-item-${text}`;

const highlightedDecorator = styled({
  backgroundColor: grey,
  color: 'black',
});

export function searchTextToWords(searchText: string) {
  return searchText.replace(/\s+/, ' ').split(' ');
}

const Base = styled.div(
  createMinLargeViewportDecorator({
    border: { top: { width: 0 } },
  }),
  {
    backgroundColor: 'white',
    overflow: 'hidden',
    height: 0,
  },
);

const Container = styled.div({
  padding: { top: t => t.spacing.xs, bottom: t => t.spacing.m },
});

const List = styled.ul(bareListDecorator);

const Suggestion = styled.li({
  padding: {
    left: 57,
    right: t => t.sharedSpacing.l,
    y: t => t.sharedSpacing.s,
  },
  fontSize: t => t.sharedFontSize.xs,
  color: t => t.searchSuggestions?.color,
  cursor: 'pointer',
});
